import React, { memo } from 'react';
export function SolutionSection() {
  return <section className="mb-16" data-id="element-55">
      <h2 className="text-2xl md:text-3xl font-bold text-teal-700 mb-6" data-id="element-56">
        A huge part of whether we feel safe — or stuck in fight, flight, or
        freeze — comes down to how well the vagus nerve is functioning.
      </h2>
      <p className="text-lg mb-6" data-id="element-57">
        The vagus nerve is like the body's internal "calm and connect" switch.
      </p>
      <p className="text-lg mb-6" data-id="element-58">
        When it's regulated and active, it sends signals of safety throughout
        the system — allowing the body to:
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-id="element-59">
        <div className="bg-teal-50 p-4 rounded-lg shadow-sm" data-id="element-60">
          <p className="flex items-center" data-id="element-61">
            <span className="text-green-500 font-bold mr-2" data-id="element-62">✅</span>
            <span className="text-lg" data-id="element-63">Release tension</span>
          </p>
        </div>
        <div className="bg-teal-50 p-4 rounded-lg shadow-sm" data-id="element-64">
          <p className="flex items-center" data-id="element-65">
            <span className="text-green-500 font-bold mr-2" data-id="element-66">✅</span>
            <span className="text-lg" data-id="element-67">Calm the heart and breath</span>
          </p>
        </div>
        <div className="bg-teal-50 p-4 rounded-lg shadow-sm" data-id="element-68">
          <p className="flex items-center" data-id="element-69">
            <span className="text-green-500 font-bold mr-2" data-id="element-70">✅</span>
            <span className="text-lg" data-id="element-71">Settle anxiety</span>
          </p>
        </div>
        <div className="bg-teal-50 p-4 rounded-lg shadow-sm" data-id="element-72">
          <p className="flex items-center" data-id="element-73">
            <span className="text-green-500 font-bold mr-2" data-id="element-74">✅</span>
            <span className="text-lg" data-id="element-75">Support restful sleep</span>
          </p>
        </div>
        <div className="bg-teal-50 p-4 rounded-lg shadow-sm" data-id="element-76">
          <p className="flex items-center" data-id="element-77">
            <span className="text-green-500 font-bold mr-2" data-id="element-78">✅</span>
            <span className="text-lg" data-id="element-79">Restore natural energy</span>
          </p>
        </div>
        <div className="bg-teal-50 p-4 rounded-lg shadow-sm" data-id="element-80">
          <p className="flex items-center" data-id="element-81">
            <span className="text-green-500 font-bold mr-2" data-id="element-82">✅</span>
            <span className="text-lg" data-id="element-83">Shift out of survival</span>
          </p>
        </div>
      </div>
      <p className="text-lg mb-6" data-id="element-84">
        But after chronic stress or trauma, this system can get dysregulated —
        leaving the body stuck in survival mode even when life is safe.
      </p>
      <div className="mb-8" data-id="element-85">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/eyUqAraF1ZubWusZgaiFtr/istockphoto-804802520-612x612.jpg" alt="Person practicing mindful breathing outdoors" className="w-full rounded-lg shadow-md" data-id="element-86" />
      </div>
      <p className="text-lg mb-6" data-id="element-87">And here's what changed everything for me:</p>
      <div className="bg-teal-50 p-6 rounded-lg my-8 border-l-4 border-teal-600" data-id="element-88">
        <p className="text-xl md:text-2xl font-semibold text-gray-700 mb-4" data-id="element-89">
          You can gently retrain the vagus nerve — and you don't have to relive
          the past to do it.
        </p>
      </div>
      <p className="text-lg mb-6" data-id="element-90">
        You don't need to "process" more to help your body feel safe again.
      </p>
      <p className="text-lg mb-6" data-id="element-91">
        You don't need to revisit every memory. You don't need to talk through
        every layer. You don't need to force yourself through intense practices.
      </p>
      <p className="text-lg mb-6" data-id="element-92">
        The key is helping the body learn, in the present, that it is safe —
        through simple, body-based signals.
      </p>
      <div className="bg-gray-100 p-6 rounded-lg my-8" data-id="element-93">
        <p className="text-xl font-medium mb-4" data-id="element-94">
          This is where vagus nerve retraining comes in.
        </p>
        <p className="text-lg mb-4" data-id="element-95">
          By using gentle, science-backed practices to activate and support the
          vagus nerve, you can:
        </p>
        <ul className="space-y-2" data-id="element-96">
          <li className="flex items-start" data-id="element-97">
            <span className="text-green-500 font-bold mr-2" data-id="element-98">✅</span>
            <span className="text-lg" data-id="element-99">Calm the nervous system</span>
          </li>
          <li className="flex items-start" data-id="element-100">
            <span className="text-green-500 font-bold mr-2" data-id="element-101">✅</span>
            <span className="text-lg" data-id="element-102">
              Shift out of fight, flight, or freeze
            </span>
          </li>
          <li className="flex items-start" data-id="element-103">
            <span className="text-green-500 font-bold mr-2" data-id="element-104">✅</span>
            <span className="text-lg" data-id="element-105">
              Create an internal felt sense of safety
            </span>
          </li>
          <li className="flex items-start" data-id="element-106">
            <span className="text-green-500 font-bold mr-2" data-id="element-107">✅</span>
            <span className="text-lg" data-id="element-108">
              Restore calm, energy, and connection
            </span>
          </li>
        </ul>
      </div>
      <p className="text-lg mb-6" data-id="element-109">And what surprised me the most?</p>
      <p className="text-lg mb-6" data-id="element-110">How gentle it was.</p>
      <p className="text-lg mb-6" data-id="element-111">
        These practices weren't about pushing or performing.
      </p>
      <p className="text-lg mb-6" data-id="element-112">
        They were about meeting my body where it was — and helping it come out
        of survival at its own pace.
      </p>
    </section>;
}